<script setup lang="ts">
  import hljs from "highlight.js";
  import "highlight.js/styles/atom-one-dark.css";
  // import katex, { KatexOptions } from "katex";
  import "katex/dist/katex.min.css";
  import { Marked } from "marked";
  import markedAlert from "marked-alert";
  // import markedCodeFormat from "marked-code-format";
  // import { markedEmoji } from "marked-emoji";
  import markedTable from "marked-extended-tables";
  import markedFootnote from "marked-footnote";
  import { gfmHeadingId } from "marked-gfm-heading-id";
  import { markedHighlight } from "marked-highlight";
  import markedKatex from "marked-katex-extension";
  import { mangle } from "marked-mangle";
  import { markedSmartypants } from "marked-smartypants";
  // import { Transformer } from "markmap-lib";
  // import { Markmap, deriveOptions } from "markmap-view";
  import mermaid from "mermaid";
  import { nextTick, onMounted, onUpdated, ref } from "vue";
  // import { Transformer } from "markmap-lib";
  // import { Markmap, deriveOptions } from "markmap-view";
  import "~/assets/css/markdown.less";
  import Common from "~/utils/common";

  defineProps<{
    text: string;
  }>();

  const renderMarkdown = (markdown: string) => {
    // remove the most common zerowidth characters from the start of the file
    // eslint-disable-next-line no-misleading-character-class
    markdown = markdown.replace(/^[\u200B\u200C\u200D\u200E\u200F\uFEFF]/, "");
    const marked = new Marked({
      async: false,
      breaks: true,
      gfm: true,
      pedantic: false,
    });
    marked.use(
      // @ts-expect-error: marked-highlight types
      markedHighlight({
        langPrefix: "hljs language-",
        highlight(code, lang) {
          // 处理 mermaid 图表
          if (lang === "mermaid" || code.match(/^sequenceDiagram/) || code.match(/^graph/)) {
            // 清理代码：去除空行、去除每行前后的空格、保持必要的缩进结构
            const cleanedCode = code
              .split("\n")
              .map((line) => line.trim()) // 去除每行的前后空格
              .filter((line) => line.length > 0) // 去除空行
              .join("\n");

            // 对代码进行 HTML 转义，避免特殊字符问题
            const escapeHtml = (text: string) => {
              const div = document.createElement("div");
              div.textContent = text;
              return div.innerHTML;
            };

            return `<pre class="mermaid" style="display: none; background-color: transparent;">${escapeHtml(cleanedCode)}</pre>`;
          }
          if (lang && hljs.getLanguage(lang)) {
            const language = hljs.getLanguage(lang) ? lang : "plaintext";
            const preCode = hljs.highlight(code, { language }).value;
            // 以换行进行分割
            const lines = preCode.split(/\n/);
            if (lines.length) {
              const codeId = Common.getUUID();
              // 添加代码语言
              const headerDiv =
                '<div class="code-block-header">' +
                `<span class="code-block-header-lang">${lang}</span>` +
                // `<span class="code-block-header-copy" onclick="handleCopy('${codeId}')">复制代码</span>` +
                `<textarea style="display:none;position: absolute;top: -9999px;left: -9999px;z-index: -9999;" id="${codeId}">${code.replace(/<\/textarea>/g, "&lt;/textarea>")}</textarea>` +
                "</div>";

              const codeList = lines.map((item, index) => {
                return `<li><span class="line-num" data-line="${index + 1}"></span>${item}</li>`;
              });

              // 添加自定义行号
              const lineDiv = `<ul>${codeList.join("")}</ul>`;
              return `<div class="code-block">${headerDiv}${lineDiv}</div>`;
            }
            return "";
          }
        },
      }),
    );
    // const markedCodeFormatOption = {
    //   tabWidth: 4,
    //   removeEmptyLines: false,
    //   semi: true,
    //   singleQuote: true,
    // };
    // marked.use(markedCodeFormat());
    marked.use(markedAlert());
    marked.use(gfmHeadingId());
    marked.use(markedFootnote());
    marked.use(markedTable());
    marked.use({
      walkTokens: (token) => {
        // putting a list inside a summary requires a double line break
        // but we shouldn't keep that double line break in the output
        // this doesn't happen in remark/rehype
        if (token.type === "html" && token.text.endsWith("</summary>\n\n")) {
          token.text = token.text.replace("</summary>\n\n", "</summary>\n");
        }
      },
    });
    marked.use(mangle());
    const markedKatexOption = {
      throwOnError: false,
      globalGroup: true,
    };
    marked.use(markedKatex(markedKatexOption));
    marked.use(markedSmartypants());

    if (typeof window !== "undefined") {
      // @ts-expect-error: handleCopy types
      window.handleCopy = handleCopy;
    }

    return marked.parse(removeHtmlTagBrackets(markdown));
  };

  // 去掉包含html标签的中括号[]
  const removeHtmlTagBrackets = (markdown: string) => {
    const regex = /\[(?=[^\]]*<\/?[\w!][^>]*>)([^\]]+)\]/g;
    return markdown.replace(regex, "$1");
  };

  const handleCopy = (id: string) => {
    const textarea = document.getElementById(id) as HTMLTextAreaElement;
    Common.handleCopyText(textarea.value);
  };

  const markdownBody = ref<HTMLElement | null>(null);

  // 初始化和渲染 Mermaid 图表
  const initMermaid = async () => {
    await nextTick();
    if (markdownBody.value) {
      const mermaidElements = markdownBody.value.querySelectorAll("pre.mermaid");
      if (mermaidElements.length > 0) {
        for (const element of Array.from(mermaidElements)) {
          const preElement = element as HTMLPreElement;

          // 如果已经渲染过，跳过
          if (preElement.getAttribute("data-processed") === "true") {
            continue;
          }

          try {
            // 获取原始代码（已经是 HTML 转义的）
            const code = preElement.textContent || "";

            await mermaid.parse(code);

            // 创建一个新的 div 来渲染 mermaid
            const container = document.createElement("div");
            container.className = "mermaid-container";
            container.style.cssText = "margin: 10px 0; background-color: #ffffff; padding: 16px; border-radius: 8px;";

            // 使用 mermaid.render 方法
            const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
            const { svg } = await mermaid.render(id, code);

            container.innerHTML = svg;

            // 查找外层的 code 和 pre 元素并设置白色背景
            let parent = preElement.parentElement;
            while (parent && parent !== markdownBody.value) {
              if (parent.tagName === "CODE" || parent.tagName === "PRE") {
                parent.style.backgroundColor = "#ffffff";
                parent.style.background = "#ffffff";
              }
              parent = parent.parentElement;
            }

            // 替换 pre 元素
            preElement.replaceWith(container);
            preElement.setAttribute("data-processed", "true");
          } catch (error) {
            console.warn("Mermaid rendering failed, fallback to normal code block:", error);
            // 回退到普通代码块渲染
            const codeId = Common.getUUID();
            const originalCode = preElement.textContent || "";

            const codeBlock = document.createElement("div");
            codeBlock.className = "code-block";

            const headerDiv = document.createElement("div");
            headerDiv.className = "code-block-header";
            headerDiv.innerHTML =
              `<span class="code-block-header-lang">mermaid</span>` +
              `<span class="code-block-header-copy" onclick="handleCopy('${codeId}')">复制代码</span>` +
              `<textarea style="display:none;position: absolute;top: -9999px;left: -9999px;z-index: -9999;" id="${codeId}">${originalCode.replace(/<\/textarea>/g, "&lt;/textarea>")}</textarea>`;

            const codeList = originalCode.split("\n").map((line, index) => {
              return `<li><span class="line-num" data-line="${index + 1}"></span>${line}</li>`;
            });

            const lineDiv = document.createElement("ul");
            lineDiv.innerHTML = codeList.join("");

            codeBlock.appendChild(headerDiv);
            codeBlock.appendChild(lineDiv);

            preElement.replaceWith(codeBlock);
            preElement.setAttribute("data-processed", "true");
          }
        }
      }
    }
  };

  onMounted(() => {
    // 初始化 mermaid 配置
    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      // logLevel: "error",
      securityLevel: "loose",
      themeVariables: {
        primaryColor: "#fff",
        primaryTextColor: "#000",
        primaryBorderColor: "#000",
        lineColor: "#000",
        secondaryColor: "#fff",
        tertiaryColor: "#fff",
        background: "#fff",
        mainBkg: "#fff",
        secondBkg: "#fff",
        tertiaryBkg: "#fff",
      },
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
      },
    });
    // 首次渲染
    initMermaid();
  });

  // 内容更新时重新渲染 mermaid
  onUpdated(() => {
    initMermaid();
  });
</script>

<template>
  <div ref="markdownBody" class="markdown-body" v-html="renderMarkdown(text)"></div>
</template>

<style scoped lang="less">
  :deep(.markdown-body) {
    a {
      color: #585ef8 !important;
    }
  }
</style>
